import { ref } from 'vue';
import { centralHttp } from '@/core/http/central';
import { dateConfig, type DateFormat, type TimeFormat } from '@/shared/utils/date';

/**
 * Public platform presentation facts, served by GET /api/platform-meta and
 * console-editable on the settings page: platform name (document titles),
 * SEO meta tags, and the date/time formats every formatDate() call renders
 * with. Fetched once at boot; all defaults hold if the request fails.
 */
export const platformName = ref('Digitron');
export const platformCurrency = ref('EUR');
export const platformAnnouncement = ref<{ message: string; level: 'info' | 'warning' } | null>(
  null,
);

interface PlatformMeta {
  name?: string;
  seo?: { title?: string; description?: string; keywords?: string };
  branding?: { logo_url?: string | null; favicon_url?: string | null; color?: string };
  announcement?: { message: string; level: 'info' | 'warning' } | null;
  date_format?: DateFormat;
  time_format?: TimeFormat;
  currency?: string;
}

let loaded = false;

export async function loadPlatformMeta(): Promise<void> {
  if (loaded) return;
  loaded = true;
  try {
    const { data } = await centralHttp.get<PlatformMeta>('/platform-meta');
    if (data.name) platformName.value = data.name;
    if (data.currency) platformCurrency.value = data.currency;
    if (data.date_format) dateConfig.dateFormat = data.date_format;
    if (data.time_format) dateConfig.timeFormat = data.time_format;
    if (data.seo?.description) upsertMetaTag('description', data.seo.description);
    if (data.seo?.keywords) upsertMetaTag('keywords', data.seo.keywords);
    if (data.branding?.favicon_url) setFavicon(data.branding.favicon_url);
    platformAnnouncement.value = data.announcement ?? null;
  } catch {
    /* central API unreachable — built-in defaults hold */
  }
}

function setFavicon(href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!el) {
    el = document.createElement('link');
    el.rel = 'icon';
    document.head.appendChild(el);
  }
  el.href = href;
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  EUR: '€',
  USD: '$',
  GBP: '£',
};

/** Display symbol for a currency code (falls back to "CODE "). */
export function currencySymbol(code?: string): string {
  const c = (code ?? platformCurrency.value).toUpperCase();
  return CURRENCY_SYMBOLS[c] ?? `${c} `;
}

function upsertMetaTag(name: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.name = name;
    document.head.appendChild(el);
  }
  el.content = content;
}
